import { useEffect, useMemo, useState } from 'react'
import {
  MessageSquare, Settings2, FileText, ListChecks, Send, KeyRound, Loader2, Check,
  RefreshCw, RotateCcw, Trash2, Download, Eye, EyeOff, Plug, AlertTriangle, Users,
  ShieldCheck, Wallet, Sparkles, Info,
} from 'lucide-react'
import { PageHead } from '../AdminLayout'
import {
  Panel, DataTable, Pager, Toolbar, Select, SearchBox, RefreshButton, Stat, Tag, Status,
  ConfirmButton, Drawer, BarList, LineChart,
} from '../ui'
import { get, post, put, del, qs } from '../../lib/api'
import { toast } from '../../store'
import { clsx, fa, faDate, num } from '../../lib/format'

const TABS: [string, string, any][] = [
  ['overview', 'نمای کلی', MessageSquare],
  ['config', 'تنظیمات سرویس', Settings2],
  ['templates', 'متن پیام‌ها', FileText],
  ['messages', 'گزارش ارسال‌ها', ListChecks],
  ['send', 'ارسال دستی و گروهی', Send],
  ['otp', 'کدهای یکبارمصرف', KeyRound],
]

const ST_KIND: Record<string, any> = {
  delivered: 'up', sent: 'info', queued: 'muted', sending: 'info',
  retry: 'gold', failed: 'down', undelivered: 'down',
}

export default function Sms() {
  const [tab, setTab] = useState('overview')
  return (
    <>
      <PageHead title="سامانه پیامک"
        subtitle="اتصال به کاوه‌نگار، متن همه پیام‌ها، گزارش ارسال و کدهای یکبارمصرف" />
      <div className="mb-4 flex flex-wrap gap-1 rounded-xl border border-white/[.07] bg-white/[.03] p-1">
        {TABS.map(([k, l, Icon]) => (
          <button key={k} onClick={() => setTab(k)}
            className={clsx('flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium transition',
              tab === k ? 'bg-gradient-to-l from-brand-600 to-brand-400 text-white'
                : 'text-slate-400 hover:bg-white/[.05] hover:text-slate-200')}>
            <Icon className="h-3.5 w-3.5" /> {l}
          </button>
        ))}
      </div>
      {tab === 'overview' && <Overview onGo={setTab} />}
      {tab === 'config' && <Config />}
      {tab === 'templates' && <Templates />}
      {tab === 'messages' && <Messages />}
      {tab === 'send' && <SendPanel />}
      {tab === 'otp' && <OtpList />}
    </>
  )
}

/* ═══════════════════════════════════════════════ نمای کلی */
function Overview({ onGo }: { onGo: (t: string) => void }) {
  const [d, setD] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [days, setDays] = useState('7')

  const load = () => {
    setLoading(true)
    get(`/admin/sms/overview${qs({ days })}`).then(setD).catch(() => {}).finally(() => setLoading(false))
  }
  useEffect(load, [days])

  if (!d) return <div className="skeleton h-64 w-full" />
  const c = d.config, s = d.stats

  return (
    <div className="space-y-4">
      {!c.ready && (
        <div className="card flex flex-wrap items-center gap-3 border-gold/30 bg-gold/[.06] p-4">
          <AlertTriangle className="h-5 w-5 text-gold" />
          <div className="flex-1 text-xs leading-6 text-slate-200">
            هنوز کلید API کاوه‌نگار ثبت نشده است؛ تا آن زمان هیچ پیامکی ارسال نمی‌شود.
          </div>
          <button className="btn-primary !px-3 !py-1.5 text-xs" onClick={() => onGo('config')}>
            <Settings2 className="h-3.5 w-3.5" /> تنظیم سرویس
          </button>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label="وضعیت سرویس" color={c.enabled && c.ready ? '#00E39A' : '#FF4D6D'}
          icon={<Plug className="h-5 w-5" />}
          value={c.enabled && c.ready ? 'فعال' : c.ready ? 'خاموش' : 'تنظیم نشده'}
          hint={c.sandbox ? 'حالت آزمایشی روشن است' : `خط: ${c.sender || '—'}`} />
        <Stat label={`پیامک ${fa(days)} روز اخیر`} color="#7C5CFF"
          icon={<MessageSquare className="h-5 w-5" />} value={fa(num(s.total))}
          hint={`${fa(num(s.queued))} در صف · ${fa(num(s.failed))} ناموفق`} />
        <Stat label="نرخ موفقیت ارسال" color="#22D3EE" icon={<Check className="h-5 w-5" />}
          value={`${fa(s.successRate)}٪`} hint={`هزینه: ${fa(num(s.cost))} ریال`} />
        <Stat label="کد یکبارمصرف" color="#FFB020" icon={<KeyRound className="h-5 w-5" />}
          value={fa(num(d.otp.total))} hint={`${fa(d.otp.successRate)}٪ تأیید موفق`} />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Panel className="lg:col-span-2" title="روند ارسال پیامک"
          action={<Select value={days} onChange={setDays}
            options={[['7', '۷ روز'], ['14', '۱۴ روز'], ['30', '۳۰ روز']]} />}>
          {/* نمودار {date, value} می‌خواهد و بک‌اند این سری را با کلید count می‌دهد */}
          <LineChart data={(d.series || []).map((p: any) => ({ date: p.date, value: p.count }))}
            color="#7C5CFF" height={150} valueFormat={(v) => `${fa(num(v))} پیامک`} />
        </Panel>
        <Panel title="پرکاربردترین پیام‌ها">
          <BarList items={(s.topEvents || []).map(([e, v]: [string, number]) => ({
            label: EVENT_FA[e] || e, value: v,
          }))} format={(v) => fa(num(v))} />
        </Panel>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="وضعیت پیام‌ها">
          <div className="flex flex-wrap gap-2">
            {Object.entries(d.statusLabels).map(([k, l]: any) => (
              <div key={k} className="rounded-xl border border-white/[.07] bg-white/[.03] px-3 py-2">
                <div className="num text-lg font-bold text-white">{fa(num(s.byStatus?.[k] || 0))}</div>
                <div className="text-[10px] text-slate-500">{l}</div>
              </div>
            ))}
          </div>
        </Panel>
        <Panel title="پیکربندی جاری">
          <div className="space-y-2 text-xs">
            <Row k="ورود با رمز عبور" v={c.passwordLoginEnabled ? 'فعال' : 'غیرفعال'} ok={c.passwordLoginEnabled} />
            <Row k="ورود با کد پیامکی" v={c.loginOtpEnabled ? 'فعال' : 'غیرفعال'} ok={c.loginOtpEnabled} />
            <Row k="تأیید شماره در ثبت‌نام" v={c.requireMobileVerify ? 'الزامی' : 'اختیاری'} ok={c.requireMobileVerify} />
            <Row k="کد پیامکی در هر ورود" v={c.smsTwoFactor ? 'الزامی' : 'اختیاری'} ok />
            <Row k="طول و اعتبار کد" v={`${fa(c.otpLength)} رقم · ${fa(c.otpTtl)} دقیقه`} ok />
            <Row k="فاصله بین دو درخواست" v={`${fa(c.otpCooldown)} ثانیه`} ok />
            <Row k="الگوهای پیام" v={`${fa(d.templates.total)} الگو (${fa(d.templates.disabled)} خاموش)`} ok />
          </div>
        </Panel>
      </div>
    </div>
  )
}

function Row({ k, v, ok }: { k: string; v: string; ok?: boolean }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-white/[.06] bg-white/[.02] px-3 py-2">
      <span className="text-slate-400">{k}</span>
      <span className={clsx('num font-medium', ok ? 'text-slate-200' : 'text-slate-500')}>{v}</span>
    </div>
  )
}

/* ═══════════════════════════════════════════════ تنظیمات سرویس */
function Toggle({ label, hint, value, onChange, disabled }: {
  label: string; hint?: string; value: boolean; onChange: (v: boolean) => void; disabled?: boolean
}) {
  return (
    <button type="button" disabled={disabled} onClick={() => onChange(!value)}
      className={clsx('flex w-full items-start justify-between gap-3 rounded-xl border p-3 text-right transition',
        value ? 'border-brand-500/35 bg-brand-500/[.07]' : 'border-white/[.06] bg-white/[.02]',
        disabled && 'opacity-50')}>
      <div className="min-w-0">
        <div className="text-xs text-slate-200">{label}</div>
        {hint && <div className="mt-1 text-[10px] leading-5 text-slate-500">{hint}</div>}
      </div>
      <span className={clsx('relative mt-0.5 h-5 w-9 shrink-0 rounded-full transition',
        value ? 'bg-gradient-to-l from-brand-600 to-brand-400' : 'bg-white/10')}>
        <span className={clsx('absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all',
          value ? 'right-0.5' : 'right-4.5')} style={{ right: value ? 2 : 18 }} />
      </span>
    </button>
  )
}

function NumField({ label, hint, value, onChange, suffix }: {
  label: string; hint?: string; value: any; onChange: (v: number) => void; suffix?: string
}) {
  return (
    <div className="rounded-xl border border-white/[.06] bg-white/[.02] p-3">
      <div className="mb-2 text-xs text-slate-200">{label}</div>
      <div className="flex items-center gap-2">
        <input className="input num !py-2 text-xs" dir="ltr" type="number" value={value}
          onChange={(e) => onChange(Number(e.target.value))} />
        {suffix && <span className="shrink-0 text-[10px] text-slate-500">{suffix}</span>}
      </div>
      {hint && <div className="mt-1.5 text-[10px] leading-5 text-slate-500">{hint}</div>}
    </div>
  )
}

function Config() {
  const [c, setC] = useState<any>(null)
  const [form, setForm] = useState<any>({})
  const [key, setKey] = useState('')
  const [showKey, setShowKey] = useState(false)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [credit, setCredit] = useState<any>(null)
  const [testMobile, setTestMobile] = useState('')
  const [sendingTest, setSendingTest] = useState(false)

  const load = () => get('/admin/sms/config').then((r: any) => {
    setC(r)
    setForm({
      enabled: r.enabled, sender: r.sender, sandbox: r.sandbox,
      notifications_enabled: r.notificationsEnabled,
      otp_length: r.otpLength, otp_ttl_min: r.otpTtl, otp_cooldown_sec: r.otpCooldown,
      otp_max_daily: r.otpMaxDaily, otp_max_attempts: r.otpMaxAttempts,
      password_login_enabled: r.passwordLoginEnabled, login_otp_enabled: r.loginOtpEnabled,
      require_mobile_verify: r.requireMobileVerify, two_factor: r.smsTwoFactor,
      withdraw_otp: r.withdrawOtp,
    })
  }).catch(() => {})
  useEffect(load, [])

  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }))

  const save = async () => {
    setSaving(true)
    try {
      const r: any = await put('/admin/sms/config', { ...form, api_key: key })
      setKey(''); setC(r.config)
      toast.ok('تنظیمات پیامک ذخیره شد')
    } catch (e: any) { toast.err(e.message) } finally { setSaving(false) }
  }

  const testConn = async () => {
    setTesting(true)
    try {
      const r: any = await post('/admin/sms/test-connection')
      setCredit(r); toast.ok('اتصال به کاوه‌نگار برقرار است')
    } catch (e: any) { toast.err(e.message) } finally { setTesting(false) }
  }

  const testSend = async () => {
    if (!testMobile) return toast.err('شماره مقصد را وارد کنید')
    setSendingTest(true)
    try {
      const r: any = await post('/admin/sms/test-send', {
        mobile: testMobile,
        message: `پیام آزمایشی ارزینو — اگر این پیام را دریافت کردید، سرویس پیامک درست کار می‌کند.`,
      })
      toast.ok(r.sandbox ? 'حالت آزمایشی — پیام واقعی ارسال نشد' : 'پیام آزمایشی ارسال شد')
    } catch (e: any) { toast.err(e.message) } finally { setSendingTest(false) }
  }

  if (!c) return <div className="skeleton h-64 w-full" />

  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="اتصال به کاوه‌نگار"
          action={<Tag kind={c.ready ? 'up' : 'gold'}>{c.ready ? 'کلید ثبت شده' : 'تنظیم نشده'}</Tag>}>
          <div className="space-y-3">
            <div>
              <label className="mb-1.5 block text-xs text-slate-300">کلید API</label>
              <div className="flex gap-2">
                <input className="input num !py-2 text-xs" dir="ltr" type={showKey ? 'text' : 'password'}
                  placeholder={c.apiKeyMasked || 'کلید API را از پنل کاوه‌نگار کپی کنید'}
                  value={key} onChange={(e) => setKey(e.target.value)} />
                <button className="btn-ghost !px-2.5" onClick={() => setShowKey((s) => !s)}>
                  {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <div className="mt-1.5 flex items-start gap-1.5 text-[10px] leading-5 text-slate-500">
                <Info className="mt-0.5 h-3 w-3 shrink-0" />
                کلید رمزنگاری‌شده ذخیره می‌شود و دیگر کامل نمایش داده نمی‌شود.
                برای حفظ کلید فعلی، این فیلد را خالی بگذارید.
              </div>
            </div>

            <div>
              <label className="mb-1.5 block text-xs text-slate-300">خط اختصاصی (شماره فرستنده)</label>
              <input className="input num !py-2 text-xs" dir="ltr" placeholder="10008663"
                value={form.sender || ''} onChange={(e) => set('sender', e.target.value)} />
            </div>

            <Toggle label="سرویس پیامک فعال است" value={!!form.enabled}
              hint="با خاموش‌کردن آن هیچ پیامکی (حتی کد ورود) ارسال نمی‌شود"
              onChange={(v) => set('enabled', v)} />
            <Toggle label="حالت آزمایشی" value={!!form.sandbox}
              hint="پیامک واقعی ارسال نمی‌شود؛ کد در پاسخ سرور برمی‌گردد تا بدون هزینه تست کنید"
              onChange={(v) => set('sandbox', v)} />
            <Toggle label="اطلاع‌رسانی‌های عمومی" value={!!form.notifications_enabled}
              hint="خاموش‌کردن آن فقط پیام‌های اطلاع‌رسانی را می‌بندد؛ کدهای امنیتی همچنان ارسال می‌شوند"
              onChange={(v) => set('notifications_enabled', v)} />

            <div className="flex flex-wrap gap-2 border-t border-white/[.06] pt-3">
              <button className="btn-ghost !px-3 !py-1.5 text-xs" onClick={testConn} disabled={testing}>
                {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plug className="h-3.5 w-3.5" />}
                تست اتصال و اعتبار
              </button>
              {credit && (
                <Tag kind="up">اعتبار: {fa(num(credit.remainCredit || 0))} ریال</Tag>
              )}
            </div>

            <div className="rounded-xl border border-white/[.06] bg-white/[.02] p-3">
              <div className="mb-2 text-xs text-slate-200">ارسال پیام آزمایشی</div>
              <div className="flex gap-2">
                <input className="input num !py-2 text-xs" dir="ltr" placeholder="09123456789"
                  value={testMobile} onChange={(e) => setTestMobile(e.target.value)} />
                <button className="btn-primary !px-3 !py-2 text-xs" onClick={testSend} disabled={sendingTest}>
                  {sendingTest ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                  ارسال
                </button>
              </div>
            </div>
          </div>
        </Panel>

        <div className="space-y-4">
          <Panel title="روش‌های ورود">
            <div className="space-y-2.5">
              <Toggle label="ورود با رمز عبور" value={!!form.password_login_enabled}
                hint="کاربر با ایمیل یا شماره موبایل و رمز عبور وارد می‌شود"
                onChange={(v) => set('password_login_enabled', v)} />
              <Toggle label="ورود با کد یکبارمصرف پیامکی" value={!!form.login_otp_enabled}
                hint="کاربر فقط شماره می‌زند و با کد پیامکی وارد می‌شود"
                onChange={(v) => set('login_otp_enabled', v)} />
              <Toggle label="تأیید شماره موبایل در ثبت‌نام" value={!!form.require_mobile_verify}
                hint="بدون تأیید کد پیامکی، ثبت‌نام کامل نمی‌شود"
                onChange={(v) => set('require_mobile_verify', v)} />
              <Toggle label="کد پیامکی در هر ورود (۲FA سراسری)" value={!!form.two_factor}
                hint="پس از رمز عبور، یک کد پیامکی هم خواسته می‌شود — برای همه کاربران"
                onChange={(v) => set('two_factor', v)} />
              <Toggle label="کد پیامکی برای تأیید برداشت" value={!!form.withdraw_otp}
                hint="هر درخواست برداشت با کد پیامکی تأیید می‌شود"
                onChange={(v) => set('withdraw_otp', v)} />
            </div>
          </Panel>

          <Panel title="پارامترهای کد یکبارمصرف">
            <div className="grid gap-3 sm:grid-cols-2">
              <NumField label="طول کد" value={form.otp_length} suffix="رقم"
                onChange={(v) => set('otp_length', v)} hint="بین ۴ تا ۸" />
              <NumField label="مدت اعتبار" value={form.otp_ttl_min} suffix="دقیقه"
                onChange={(v) => set('otp_ttl_min', v)} />
              <NumField label="فاصله بین دو درخواست" value={form.otp_cooldown_sec} suffix="ثانیه"
                onChange={(v) => set('otp_cooldown_sec', v)} />
              <NumField label="سقف کد در ۲۴ ساعت" value={form.otp_max_daily} suffix="بار"
                onChange={(v) => set('otp_max_daily', v)} hint="برای هر شماره موبایل" />
              <NumField label="حداکثر تلاش نادرست" value={form.otp_max_attempts} suffix="بار"
                onChange={(v) => set('otp_max_attempts', v)} />
            </div>
          </Panel>
        </div>
      </div>

      <div className="sticky bottom-4 flex justify-end">
        <button className="btn-primary shadow-card" onClick={save} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          ذخیره تنظیمات
        </button>
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════ متن پیام‌ها */
const EVENT_FA: Record<string, string> = {
  otp_register: 'کد ثبت‌نام', otp_login: 'کد ورود', otp_withdraw: 'کد برداشت',
  otp_reset: 'کد بازیابی رمز', otp_change_mobile: 'کد تغییر شماره',
  welcome: 'خوش‌آمدگویی', login_alert: 'هشدار ورود', password_changed: 'تغییر رمز',
  twofa_changed: 'تغییر ۲FA', account_blocked: 'مسدودی حساب', account_unblocked: 'رفع مسدودی',
  kyc_approved: 'تأیید احراز هویت', kyc_rejected: 'رد احراز هویت',
  deposit_crypto: 'واریز رمزارز', deposit_crypto_pending: 'واریز در انتظار', deposit_rial: 'واریز ریالی',
  withdraw_crypto_requested: 'ثبت برداشت رمزارز', withdraw_crypto_sent: 'ارسال برداشت',
  withdraw_crypto_failed: 'برداشت ناموفق', withdraw_rial_requested: 'ثبت برداشت ریالی',
  withdraw_rial_paid: 'پرداخت برداشت ریالی', withdraw_rial_rejected: 'رد برداشت ریالی',
  order_filled: 'انجام سفارش', price_alert: 'هشدار قیمت', stake_reward: 'سود استیکینگ',
  ticket_reply: 'پاسخ تیکت', admin_message: 'پیام مدیریت', manual: 'ارسال دستی',
  bulk: 'ارسال گروهی', test: 'پیام آزمایشی',
}

function Templates() {
  const [d, setD] = useState<any>(null)
  const [edit, setEdit] = useState<any>(null)
  const [body, setBody] = useState('')
  const [preview, setPreview] = useState('')
  const [saving, setSaving] = useState(false)

  const load = () => get('/admin/sms/templates').then(setD).catch(() => {})
  useEffect(load, [])

  const open = (t: any) => { setEdit(t); setBody(t.body); setPreview('') }

  useEffect(() => {
    if (!edit) return
    const id = setTimeout(() => {
      post(`/admin/sms/templates/${edit.event}/preview`, { body })
        .then((r: any) => setPreview(r.preview)).catch(() => {})
    }, 350)
    return () => clearTimeout(id)
  }, [body, edit])

  const save = async () => {
    setSaving(true)
    try {
      await put(`/admin/sms/templates/${edit.event}`, { body })
      toast.ok('متن پیام ذخیره شد'); setEdit(null); load()
    } catch (e: any) { toast.err(e.message) } finally { setSaving(false) }
  }

  const toggle = async (t: any) => {
    try { await put(`/admin/sms/templates/${t.event}`, { enabled: !t.enabled }); load() }
    catch (e: any) { toast.err(e.message) }
  }

  const reset = async () => {
    try {
      const r: any = await post(`/admin/sms/templates/${edit.event}/reset`)
      setBody(r.body); toast.ok('متن به حالت پیش‌فرض بازگشت'); load()
    } catch (e: any) { toast.err(e.message) }
  }

  if (!d) return <div className="skeleton h-64 w-full" />
  const chars = body.length
  const parts = Math.max(1, Math.ceil(chars / 70))

  return (
    <div className="space-y-4">
      <div className="card flex flex-wrap items-center gap-2 p-3 text-[11px] text-slate-400">
        <Sparkles className="h-4 w-4 text-brand-300" />
        متغیرهای قابل استفاده در متن‌ها:
        {d.variables.map((v: string) => (
          <code key={v} className="num rounded-md border border-white/10 bg-white/[.04] px-1.5 py-0.5 text-brand-200">
            {`{${v}}`}
          </code>
        ))}
      </div>

      {d.groups.map((g: any) => (
        <Panel key={g.group} title={g.group} dense>
          <div className="divide-y divide-white/[.05]">
            {g.items.map((t: any) => (
              <div key={t.event} className="flex flex-wrap items-start gap-3 p-4">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-white">{t.title}</span>
                    <code className="num rounded-md bg-white/[.04] px-1.5 py-0.5 text-[10px] text-slate-500">{t.event}</code>
                    {t.critical && <Tag kind="gold">امنیتی</Tag>}
                    {!t.enabled && <Tag kind="down">خاموش</Tag>}
                    {t.method === 'lookup' && <Tag kind="info">الگوی کاوه‌نگار</Tag>}
                  </div>
                  <p className="mt-1.5 whitespace-pre-wrap text-[11px] leading-6 text-slate-400">{t.body}</p>
                  <div className="num mt-1.5 text-[10px] text-slate-600">
                    {fa(t.length)} کاراکتر · {fa(t.parts)} بخش پیامک
                  </div>
                </div>
                <div className="flex shrink-0 gap-2">
                  <button className="btn-ghost !px-3 !py-1.5 text-xs" onClick={() => open(t)}>ویرایش متن</button>
                  <button className={clsx('!px-3 !py-1.5 text-xs', t.enabled ? 'btn-ghost' : 'btn-up')}
                    onClick={() => toggle(t)} disabled={t.critical}>
                    {t.enabled ? 'خاموش' : 'روشن'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      ))}

      <Drawer open={!!edit} onClose={() => setEdit(null)} wide
        title={edit ? `ویرایش متن — ${edit.title}` : ''}>
        {edit && (
          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-xs text-slate-300">متن پیامک</label>
              <textarea className="input min-h-[160px] text-xs leading-7" value={body}
                onChange={(e) => setBody(e.target.value)} />
              <div className="num mt-1.5 flex justify-between text-[10px] text-slate-500">
                <span>{fa(chars)} کاراکتر</span>
                <span className={parts > 2 ? 'text-gold' : ''}>{fa(parts)} بخش پیامک</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-1.5">
              {(d.variables || []).map((v: string) => (
                <button key={v} onClick={() => setBody((b) => `${b}{${v}}`)}
                  className="num rounded-md border border-white/10 bg-white/[.04] px-2 py-1 text-[10px] text-brand-200 hover:bg-brand-500/15">
                  {`{${v}}`}
                </button>
              ))}
            </div>

            <div>
              <div className="mb-1.5 text-xs text-slate-300">پیش‌نمایش با داده نمونه</div>
              <div className="whitespace-pre-wrap rounded-xl border border-brand-500/25 bg-brand-500/[.06] p-3 text-xs leading-7 text-slate-100">
                {preview || '…'}
              </div>
            </div>

            <div className="flex justify-between gap-2 border-t border-white/[.06] pt-4">
              <ConfirmButton label="بازگشت به متن پیش‌فرض" variant="ghost" icon={<RotateCcw className="h-3.5 w-3.5" />}
                question="متن پیش‌فرض جایگزین شود؟" onConfirm={reset} />
              <button className="btn-primary !px-4 !py-2 text-xs" onClick={save} disabled={saving}>
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                ذخیره
              </button>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  )
}

/* ═══════════════════════════════════════════════ گزارش ارسال‌ها */
function Messages() {
  const [d, setD] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [status, setStatus] = useState('')
  const [event, setEvent] = useState('')
  const [q, setQ] = useState('')
  const [page, setPage] = useState(1)
  const [view, setView] = useState<any>(null)

  const load = () => {
    setLoading(true)
    get(`/admin/sms/messages${qs({ status, event, q, page, per_page: 25 })}`)
      .then(setD).catch(() => {}).finally(() => setLoading(false))
  }
  useEffect(() => { const t = setTimeout(load, q ? 400 : 0); return () => clearTimeout(t) }, [status, event, q, page])

  const retry = async (id: number) => {
    try { await post(`/admin/sms/messages/${id}/retry`); toast.ok('پیام دوباره در صف قرار گرفت'); load() }
    catch (e: any) { toast.err(e.message) }
  }

  return (
    <>
      <Toolbar>
        <SearchBox value={q} onChange={(v) => { setQ(v); setPage(1) }} placeholder="شماره، متن یا شناسه پیامک…" />
        <Select value={status} onChange={(v) => { setStatus(v); setPage(1) }}
          options={[['', 'همه وضعیت‌ها'], ['queued', 'در صف'], ['sent', 'ارسال شد'],
            ['delivered', 'رسید به گیرنده'], ['retry', 'تلاش مجدد'],
            ['undelivered', 'تحویل نشد'], ['failed', 'ناموفق']]} />
        <Select value={event} onChange={(v) => { setEvent(v); setPage(1) }}
          options={[['', 'همه رویدادها'], ...((d?.events || []).map((e: string) => [e, EVENT_FA[e] || e]))] as any} />
        <RefreshButton onClick={load} loading={loading} />
        <a className="btn-ghost !px-2.5 !py-2 text-xs" download
          href={`/api/admin/sms/messages/export${qs({ status, event, q })}`}>
          <Download className="h-3.5 w-3.5" /> خروجی CSV
        </a>
      </Toolbar>

      <Panel dense>
        <DataTable rows={d?.data ?? null} loading={loading} minWidth={980} onRowClick={setView}
          columns={[
            { key: 'id', header: '#', cell: (r: any) => <span className="num text-slate-500">{fa(r.id)}</span> },
            { key: 'mobile', header: 'گیرنده', cell: (r: any) => (
              <div>
                <div className="num text-slate-200" dir="ltr">{r.mobile}</div>
                <div className="num text-[10px] text-slate-500" dir="ltr">{r.email}</div>
              </div>
            ) },
            { key: 'event', header: 'رویداد', cell: (r: any) => <Tag>{EVENT_FA[r.event] || r.event}</Tag> },
            { key: 'body', header: 'متن', cell: (r: any) => (
              <div className="max-w-[280px] truncate text-[11px] text-slate-400">{r.body.replace(/\n/g, ' ')}</div>
            ) },
            { key: 'status', header: 'وضعیت', cell: (r: any) => (
              <div className="flex flex-col gap-1">
                <Tag kind={ST_KIND[r.status] || 'muted'}>{r.statusLabel}</Tag>
                {r.error && <span className="max-w-[180px] truncate text-[10px] text-down">{r.error}</span>}
              </div>
            ) },
            { key: 'cost', header: 'هزینه', cell: (r: any) => <span className="num text-slate-400">{fa(num(r.cost))}</span> },
            { key: 'at', header: 'زمان', cell: (r: any) => <span className="text-[11px] text-slate-500">{faDate(r.createdAt, true)}</span> },
            { key: 'act', header: '', cell: (r: any) => (
              (r.status === 'failed' || r.status === 'undelivered') ? (
                <button className="btn-ghost !px-2 !py-1 text-[10px]"
                  onClick={(e) => { e.stopPropagation(); retry(r.id) }}>
                  <RefreshCw className="h-3 w-3" /> تلاش مجدد
                </button>
              ) : null
            ) },
          ]} />
        {d?.meta && <Pager meta={d.meta} onPage={setPage} />}
      </Panel>

      <Drawer open={!!view} onClose={() => setView(null)} title="جزئیات پیامک">
        {view && (
          <div className="space-y-3 text-xs">
            <div className="whitespace-pre-wrap rounded-xl border border-white/[.06] bg-white/[.03] p-3 leading-7 text-slate-100">
              {view.body}
            </div>
            <Row k="گیرنده" v={view.mobile} ok />
            <Row k="کاربر" v={view.email} ok />
            <Row k="رویداد" v={EVENT_FA[view.event] || view.event} ok />
            <Row k="وضعیت" v={view.statusLabel} ok />
            <Row k="وضعیت اپراتور" v={view.providerStatusLabel || '—'} ok />
            <Row k="خط فرستنده" v={view.sender || '—'} ok />
            <Row k="شناسه کاوه‌نگار" v={view.providerId || '—'} ok />
            <Row k="هزینه" v={`${fa(num(view.cost))} ریال`} ok />
            <Row k="تعداد تلاش" v={fa(view.attempts)} ok />
            <Row k="ثبت" v={faDate(view.createdAt, true)} ok />
            <Row k="ارسال" v={view.sentAt ? faDate(view.sentAt, true) : '—'} ok />
            <Row k="تحویل" v={view.deliveredAt ? faDate(view.deliveredAt, true) : '—'} ok />
            {view.error && (
              <div className="rounded-xl border border-down/25 bg-down/[.07] p-3 leading-6 text-down">{view.error}</div>
            )}
            <div className="flex justify-between gap-2 border-t border-white/[.06] pt-3">
              <ConfirmButton label="حذف از گزارش" icon={<Trash2 className="h-3.5 w-3.5" />}
                onConfirm={async () => {
                  try { await del(`/admin/sms/messages/${view.id}`); toast.ok('حذف شد'); setView(null); load() }
                  catch (e: any) { toast.err(e.message) }
                }} />
              <button className="btn-ghost !px-3 !py-1.5 text-xs" onClick={() => retry(view.id)}>
                <RefreshCw className="h-3.5 w-3.5" /> ارسال مجدد
              </button>
            </div>
          </div>
        )}
      </Drawer>
    </>
  )
}

/* ═══════════════════════════════════════════════ ارسال دستی و گروهی */
function SendPanel() {
  const [mobile, setMobile] = useState('')
  const [msg, setMsg] = useState('')
  const [busy, setBusy] = useState(false)

  const [audience, setAudience] = useState('verified')
  const [tag, setTag] = useState('')
  const [kycLevel, setKycLevel] = useState(1)
  const [list, setList] = useState('')
  const [bulkMsg, setBulkMsg] = useState('')
  const [count, setCount] = useState<number | null>(null)
  const [bulkBusy, setBulkBusy] = useState(false)

  const payload = useMemo(() => ({
    message: bulkMsg, audience, tag, kyc_level: kycLevel,
    mobiles: list.split(/[\s,،;]+/).filter(Boolean),
  }), [bulkMsg, audience, tag, kycLevel, list])

  useEffect(() => {
    const t = setTimeout(() => {
      post('/admin/sms/bulk/preview', { ...payload, message: 'x' })
        .then((r: any) => setCount(r.count)).catch(() => setCount(null))
    }, 350)
    return () => clearTimeout(t)
  }, [audience, tag, kycLevel, list])

  const sendOne = async () => {
    if (!mobile || !msg.trim()) return toast.err('شماره و متن پیام لازم است')
    setBusy(true)
    try {
      await post('/admin/sms/send', { mobile, message: msg })
      toast.ok('پیام در صف ارسال قرار گرفت'); setMsg('')
    } catch (e: any) { toast.err(e.message) } finally { setBusy(false) }
  }

  const sendBulk = async () => {
    if (!bulkMsg.trim()) return toast.err('متن پیام را بنویسید')
    setBulkBusy(true)
    try {
      const r: any = await post('/admin/sms/bulk', payload)
      toast.ok(`${fa(r.queued)} پیامک در صف ارسال قرار گرفت`); setBulkMsg('')
    } catch (e: any) { toast.err(e.message) } finally { setBulkBusy(false) }
  }

  const chars = (t: string) => `${fa(t.length)} کاراکتر · ${fa(Math.max(1, Math.ceil(t.length / 70)))} بخش`

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Panel title="ارسال به یک شماره">
        <div className="space-y-3">
          <div>
            <label className="mb-1.5 block text-xs text-slate-300">شماره موبایل</label>
            <input className="input num !py-2 text-xs" dir="ltr" placeholder="09123456789"
              value={mobile} onChange={(e) => setMobile(e.target.value)} />
          </div>
          <div>
            <label className="mb-1.5 block text-xs text-slate-300">متن پیام</label>
            <textarea className="input min-h-[120px] text-xs leading-7" value={msg}
              onChange={(e) => setMsg(e.target.value)} />
            <div className="num mt-1 text-[10px] text-slate-500">{chars(msg)}</div>
          </div>
          <button className="btn-primary w-full !py-2 text-xs" onClick={sendOne} disabled={busy}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            ارسال پیامک
          </button>
        </div>
      </Panel>

      <Panel title="ارسال گروهی"
        action={count !== null && <Tag kind="info">{fa(num(count))} گیرنده</Tag>}>
        <div className="space-y-3">
          <div>
            <label className="mb-1.5 block text-xs text-slate-300">گروه گیرندگان</label>
            <Select className="!w-full" value={audience} onChange={setAudience}
              options={[['verified', 'کاربران با شماره تأییدشده'], ['active', 'کاربران فعال'],
                ['kyc', 'کاربران احراز هویت‌شده'], ['all', 'همه کاربران دارای شماره'],
                ['tag', 'کاربران دارای برچسب'], ['list', 'فهرست دستی شماره‌ها']]} />
          </div>
          {audience === 'tag' && (
            <input className="input !py-2 text-xs" placeholder="برچسب کاربر"
              value={tag} onChange={(e) => setTag(e.target.value)} />
          )}
          {audience === 'kyc' && (
            <Select className="!w-full" value={String(kycLevel)} onChange={(v) => setKycLevel(Number(v))}
              options={[['1', 'سطح ۱ به بالا'], ['2', 'سطح ۲ به بالا'], ['3', 'سطح ۳']]} />
          )}
          {audience === 'list' && (
            <textarea className="input num min-h-[90px] text-xs" dir="ltr"
              placeholder="09120000000, 09350000000" value={list} onChange={(e) => setList(e.target.value)} />
          )}
          <div>
            <label className="mb-1.5 block text-xs text-slate-300">متن پیام</label>
            <textarea className="input min-h-[120px] text-xs leading-7" value={bulkMsg}
              onChange={(e) => setBulkMsg(e.target.value)} />
            <div className="num mt-1 text-[10px] text-slate-500">{chars(bulkMsg)}</div>
          </div>
          <div className="flex items-start gap-1.5 rounded-lg border border-gold/25 bg-gold/[.06] p-2.5 text-[10px] leading-5 text-slate-300">
            <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0 text-gold" />
            کاربرانی که اعلان پیامکی خود را خاموش کرده‌اند در ارسال گروهی نادیده گرفته می‌شوند.
          </div>
          <ConfirmButton className="w-full !justify-center" variant="up"
            icon={<Users className="h-3.5 w-3.5" />}
            label={bulkBusy ? 'در حال ارسال…' : `ارسال به ${fa(num(count || 0))} نفر`}
            question="ارسال گروهی انجام شود؟" onConfirm={sendBulk} />
        </div>
      </Panel>
    </div>
  )
}

/* ═══════════════════════════════════════════════ کدهای یکبارمصرف */
function OtpList() {
  const [d, setD] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [purpose, setPurpose] = useState('')
  const [q, setQ] = useState('')
  const [page, setPage] = useState(1)

  const load = () => {
    setLoading(true)
    get(`/admin/sms/otp${qs({ purpose, q, page, per_page: 25 })}`)
      .then(setD).catch(() => {}).finally(() => setLoading(false))
  }
  useEffect(() => { const t = setTimeout(load, q ? 400 : 0); return () => clearTimeout(t) }, [purpose, q, page])

  const PURPOSE: Record<string, string> = {
    register: 'ثبت‌نام', login: 'ورود', reset: 'بازیابی رمز',
    withdraw: 'برداشت', change_mobile: 'تغییر شماره',
  }

  return (
    <>
      <Toolbar>
        <SearchBox value={q} onChange={(v) => { setQ(v); setPage(1) }} placeholder="شماره موبایل…" />
        <Select value={purpose} onChange={(v) => { setPurpose(v); setPage(1) }}
          options={[['', 'همه موارد'], ...Object.entries(PURPOSE)] as any} />
        <RefreshButton onClick={load} loading={loading} />
        <ConfirmButton label="پاک‌سازی کدهای قدیمی" icon={<Trash2 className="h-3.5 w-3.5" />}
          question="کدهای بیش از ۳ روز حذف شوند؟"
          onConfirm={async () => {
            try { const r: any = await post('/admin/sms/otp/purge?days=3'); toast.ok(`${fa(r.removed)} رکورد حذف شد`); load() }
            catch (e: any) { toast.err(e.message) }
          }} />
      </Toolbar>

      <div className="card mb-4 flex items-start gap-2 p-3 text-[11px] leading-6 text-slate-400">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-up" />
        خودِ کدها هرگز ذخیره نمی‌شوند — فقط اثر انگشت رمزنگاری‌شده آن‌ها در دیتابیس است،
        بنابراین حتی مدیر سامانه هم نمی‌تواند کد کاربر را ببیند.
      </div>

      <Panel dense>
        <DataTable rows={d?.data ?? null} loading={loading} minWidth={860}
          columns={[
            { key: 'id', header: '#', cell: (r: any) => <span className="num text-slate-500">{fa(r.id)}</span> },
            { key: 'mobile', header: 'شماره', cell: (r: any) => (
              <span className="num text-slate-200" dir="ltr">{r.mobile}</span>
            ) },
            { key: 'email', header: 'کاربر', cell: (r: any) => (
              <span className="num text-[11px] text-slate-500" dir="ltr">{r.email}</span>
            ) },
            { key: 'purpose', header: 'مورد', cell: (r: any) => <Tag>{PURPOSE[r.purpose] || r.purpose}</Tag> },
            { key: 'state', header: 'وضعیت', cell: (r: any) => (
              r.used ? <Tag kind="up">تأیید شد</Tag>
                : r.expired ? <Tag kind="muted">منقضی</Tag> : <Tag kind="gold">در انتظار</Tag>
            ) },
            { key: 'attempts', header: 'تلاش نادرست', cell: (r: any) => (
              <span className={clsx('num', r.attempts > 2 ? 'text-down' : 'text-slate-400')}>{fa(r.attempts)}</span>
            ) },
            { key: 'ip', header: 'IP', cell: (r: any) => <span className="num text-[11px] text-slate-500" dir="ltr">{r.ip}</span> },
            { key: 'at', header: 'زمان', cell: (r: any) => <span className="text-[11px] text-slate-500">{faDate(r.createdAt, true)}</span> },
          ]} />
        {d?.meta && <Pager meta={d.meta} onPage={setPage} />}
      </Panel>
    </>
  )
}
